/**
 * Werktage und Versandfristen (Europe/Berlin): Mo–Fr ohne Feiertage – bundesweit, oder mit Bundesland des Lagers
 * zusätzlich dessen Landesfeiertage. Grundlage für „pünktlich versendet“ (Leistungskennzahlen) und die Lieferzeit-Anzeige.
 */
import { stateHolidays, type DeState } from "./holidays.js";

const TZ = "Europe/Berlin";

/** YYYY-MM-DD in Berlin */
export const berlinYmd = (d: Date) => d.toLocaleDateString("sv-SE", { timeZone: TZ });
/** HH:MM in Berlin */
export const berlinHm = (d: Date) => d.toLocaleTimeString("de-DE", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false });

/** Mitternacht (Beginn des Tages) in Berlin als Zeitpunkt */
export function berlinMidnight(ymd: string): Date {
  const probe = new Date(`${ymd}T12:00:00Z`);
  const offsetH = Number(probe.toLocaleString("en-US", { timeZone: TZ, hour: "2-digit", hour12: false })) - 12;
  return new Date(new Date(`${ymd}T00:00:00Z`).getTime() - offsetH * 3_600_000);
}

const addDays = (ymd: string, n: number) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Ostersonntag (Gauß/Meeus) */
function easter(year: number): string {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const holidayCache = new Map<number, Set<string>>();
/** bundesweite Feiertage */
export function germanHolidays(year: number): Set<string> {
  let s = holidayCache.get(year);
  if (!s) {
    const e = easter(year);
    s = new Set([`${year}-01-01`, addDays(e, -2), addDays(e, 1), `${year}-05-01`, addDays(e, 39), addDays(e, 50), `${year}-10-03`, `${year}-12-25`, `${year}-12-26`]);
    holidayCache.set(year, s);
  }
  return s;
}

const stateCache = new Map<string, Set<string>>();
/** Feiertage eines Jahres: bundesweit, mit Bundesland zusätzlich die Landesfeiertage */
function holidaysFor(year: number, region?: DeState | null): Set<string> {
  if (!region) return germanHolidays(year);
  const k = `${region}:${year}`;
  let set = stateCache.get(k);
  if (!set) {
    set = new Set(stateHolidays(year, region).map((h) => h.date));
    stateCache.set(k, set);
  }
  return set;
}

/** Werktag? Mit Bundesland des Lagers zählen auch dessen Landesfeiertage (z. B. Allerheiligen in Bayern) */
export function isWorkday(ymd: string, region?: DeState | null): boolean {
  const dow = new Date(`${ymd}T12:00:00Z`).getUTCDay();
  return dow !== 0 && dow !== 6 && !holidaysFor(Number(ymd.slice(0, 4)), region).has(ymd);
}

export function nextWorkday(ymd: string, region?: DeState | null): string {
  let d = addDays(ymd, 1);
  while (!isWorkday(d, region)) d = addDays(d, 1);
  return d;
}

/** Standard-Annahmeschluss, wenn das Lager für die Versandart keinen meldet */
export const DEFAULT_CUTOFF = "14:00";
const normCutoff = (c: string | null | undefined) => (c && /^\d{1,2}:\d{2}/.test(c) ? c.slice(0, 5).padStart(5, "0") : DEFAULT_CUTOFF);

/**
 * Versandfrist eines an das Lager übergebenen Auftrags: an einem Werktag bis zum Annahmeschluss übergeben → Versand
 * am selben Tag, sonst am nächsten Werktag. Frist = Ende dieses Tages (Berlin).
 */
export function shipDeadline(handedOverAt: Date, cutoff?: string | null, region?: DeState | null): { day: string; deadline: Date } {
  const ymd = berlinYmd(handedOverAt);
  const day = isWorkday(ymd, region) && berlinHm(handedOverAt) <= normCutoff(cutoff) ? ymd : nextWorkday(ymd, region);
  return { day, deadline: new Date(berlinMidnight(addDays(day, 1)).getTime() - 1) };
}

export { addDays as addDaysYmd };
